import { environment } from './environment';

/**
 * Configuration Stripe depuis les variables d'environnement
 * Format: VITE_STRIPE_PUBLIC_KEY_<ORGANIZATION_ID> et VITE_STRIPE_BRAND_NAME_<ORGANIZATION_ID>
 */
interface StripeOrgConfig {
  publicKey: string;
  brandName: string;
}

/**
 * Récupère la configuration Stripe depuis les variables d'environnement
 */
function getStripeConfigFromEnv(): Record<string, StripeOrgConfig> {
  const config: Record<string, StripeOrgConfig> = {};
  
  // Par défaut, utiliser les variables sans suffixe d'organisation
  const defaultPublicKey = import.meta.env.VITE_STRIPE_PUBLIC_KEY;
  const defaultBrandName = import.meta.env.VITE_STRIPE_BRAND_NAME || 'CashLive';
  
  if (defaultPublicKey) {
    config.cashlive = {
      publicKey: defaultPublicKey,
      brandName: defaultBrandName,
    };
  }
  
  // Chercher des configurations spécifiques par organisation
  // Format: VITE_STRIPE_PUBLIC_KEY_<ORG_ID> et VITE_STRIPE_BRAND_NAME_<ORG_ID>
  const env = import.meta.env;
  const orgIds = new Set<string>();
  
  for (const key in env) {
    if (key.startsWith('VITE_STRIPE_PUBLIC_KEY_')) {
      const orgId = key.replace('VITE_STRIPE_PUBLIC_KEY_', '').toLowerCase();
      orgIds.add(orgId);
    }
  }
  
  for (const orgId of orgIds) {
    const publicKey = env[`VITE_STRIPE_PUBLIC_KEY_${orgId.toUpperCase()}`];
    const brandName = env[`VITE_STRIPE_BRAND_NAME_${orgId.toUpperCase()}`] || orgId;
    
    if (publicKey) {
      config[orgId] = {
        publicKey,
        brandName,
      };
    }
  }
  
  return config;
}

// Cache de la configuration Stripe
let stripeConfigCache: Record<string, StripeOrgConfig> | null = null;

function getBaseStripeConfig(): Record<string, StripeOrgConfig> {
  if (!stripeConfigCache) {
    stripeConfigCache = getStripeConfigFromEnv();
  }
  return stripeConfigCache;
}

// Get Stripe configuration - dynamique basé sur l'organisation
export const getStripePublicKey = (organizationId?: string): string => {
  const config = getBaseStripeConfig();
  
  // Si un organizationId est fourni et qu'une config existe pour cette org
  if (organizationId && config[organizationId.toLowerCase()]) {
    return config[organizationId.toLowerCase()].publicKey;
  }
  
  // Sinon, retourner la première clé disponible (fallback)
  const firstKey = Object.keys(config)[0];
  if (firstKey && config[firstKey]) {
    return config[firstKey].publicKey;
  }
  
  // Si aucune config n'est trouvée, lancer une erreur
  throw new Error(
    `Configuration Stripe manquante. Veuillez définir VITE_STRIPE_PUBLIC_KEY dans votre fichier .env`
  );
};

// Export configuration for direct access
export const getStripeConfig = (organizationId?: string) => {
  const config = getBaseStripeConfig();
  
  // Si un organizationId est fourni et qu'une config existe pour cette org
  if (organizationId && config[organizationId.toLowerCase()]) {
    const orgConfig = config[organizationId.toLowerCase()];
    return {
      publicKey: orgConfig.publicKey,
      brandName: orgConfig.brandName,
      isProduction: !environment.isDevelopment
    };
  }
  
  // Sinon, retourner la première config disponible (fallback)
  const firstKey = Object.keys(config)[0];
  if (firstKey && config[firstKey]) {
    return {
      publicKey: config[firstKey].publicKey,
      brandName: config[firstKey].brandName,
      isProduction: !environment.isDevelopment
    };
  }
  
  // Si aucune config n'est trouvée, lancer une erreur
  throw new Error(
    `Configuration Stripe manquante. Veuillez définir VITE_STRIPE_PUBLIC_KEY dans votre fichier .env`
  );
};

// Log current Stripe configuration for debugging
if (environment.isDevelopment) {
  console.log(`💳 Stripe configured for ${environment.isDevelopment ? 'DEVELOPMENT' : 'PRODUCTION'} mode`);
  console.log(`💳 Configuration dynamique basée sur l'organizationId`);
  const config = getBaseStripeConfig();
  console.log(`💳 Organisations configurées: ${Object.keys(config).join(', ')}`);
}